import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { COLLECTION_CONFIGS } from '@/utils/dataConfigs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { db } from '@/lib/firebase';
import { collection, query, getDocs, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';

export default function Export({
    isOpen,
    onClose,
    targetCollection = 'deal_audits',
    companyId,
    customData = null // This is the 'Current view' data
}) {
    const { user } = useAuth();
    const config = COLLECTION_CONFIGS[targetCollection] || COLLECTION_CONFIGS.deal_audits;
    
    // State
    const [exportName, setExportName] = useState(`${config.label} Export`);
    const [format, setFormat] = useState('XLSX'); // XLSX, CSV, PDF, HTML, DASHBOARD
    const [dataScope, setDataScope] = useState('CURRENT_VIEW'); // CURRENT_VIEW, EVERYTHING
    const [isExporting, setIsExporting] = useState(false);
    const [showCustomize, setShowCustomize] = useState(false);
    const [selectedFields, setSelectedFields] = useState(config.fields.map(f => f.key));

    const handleFieldToggle = (key) => {
        setSelectedFields(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const generateHTML = (data, fields) => {
        const headers = fields.map(f => f.label);
        const keys = fields.map(f => f.key);

        let tableRows = data.map(row => {
            let cells = keys.map(k => {
                let val = row[k] ?? '';
                if (val?.seconds) val = new Date(val.seconds * 1000).toLocaleDateString();
                if (val?.toDate) val = val.toDate().toLocaleDateString();
                return `<td>${String(val).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`;
            }).join('');
            return `<tr>${cells}</tr>`;
        }).join('\n');

        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>${exportName}</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; padding: 2rem; color: #333; background: #fff; }
        h1 { font-size: 24px; font-weight: 600; margin-bottom: 24px; }
        table { width: 100%; border-collapse: collapse; font-size: 14px; }
        th { text-align: left; padding: 12px; border-bottom: 2px solid #eaeaea; color: #666; font-weight: 500; }
        td { padding: 12px; border-bottom: 1px solid #eaeaea; }
        tr:hover { background: #f9f9f9; }
    </style>
</head>
<body>
    <h1>${exportName}</h1>
    <table>
        <thead>
            <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
        </thead>
        <tbody>
            ${tableRows}
        </tbody>
    </table>
</body>
</html>`;
    };

    const handleExport = async () => {
        if (!companyId && !customData && dataScope === 'EVERYTHING') return;
        
        const activeFields = config.fields.filter(f => selectedFields.includes(f.key));
        const ext = format === 'DASHBOARD' ? 'pdf' : format.toLowerCase();
        const defaultFileName = `${exportName.replace(/\.[^/.]+$/, "")}.${ext}`;
        
        let fileHandle = null;
        
        // 1. Ask for file location FIRST (captures user gesture securely)
        if (window.showSaveFilePicker) {
            try {
                fileHandle = await window.showSaveFilePicker({
                    suggestedName: defaultFileName,
                    types: [{
                        description: `${format} File`,
                        accept: { '*/*': [`.${ext}`] }
                    }]
                });
            } catch (err) {
                if (err.name === 'AbortError') return; // User cancelled Save dialog
            }
        }

        setIsExporting(true);

        try {
            // 2. Fetch Data
            let data = [];
            if (dataScope === 'CURRENT_VIEW' && customData) {
                data = customData;
            } else {
                const q = query(collection(db, 'companies', companyId, targetCollection), orderBy('createdAt', 'desc'));
                const snap = await getDocs(q);
                data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            }

            // 3. Build File Buffer
            let finalBuffer = null;
            let mimeType = '';

            if (format === 'PDF' || format === 'DASHBOARD') {
                const doc = format === 'DASHBOARD' ? new jsPDF('p', 'mm', 'a4') : new jsPDF('l', 'mm', 'a4');
                const headers = activeFields.map(f => f.label);
                const keys = activeFields.map(f => f.key);
                
                const tableData = data.map(row => keys.map(k => {
                    let val = row[k] ?? '';
                    if (val?.seconds) val = new Date(val.seconds * 1000).toLocaleDateString();
                    if (val?.toDate) val = val.toDate().toLocaleDateString();
                    return String(val);
                }));

                autoTable(doc, {
                    head: [headers],
                    body: tableData,
                    startY: 20,
                    theme: 'grid',
                    styles: { fontSize: 8 }
                });
                finalBuffer = doc.output('arraybuffer');
                mimeType = 'application/pdf';
                
            } else if (format === 'XLSX') {
                const exportData = data.map(row => {
                    const obj = {};
                    activeFields.forEach(f => {
                        let val = row[f.key] ?? '';
                        if (val?.seconds) val = new Date(val.seconds * 1000).toLocaleDateString();
                        if (val?.toDate) val = val.toDate().toLocaleDateString();
                        obj[f.label] = val;
                    });
                    return obj;
                });
                const worksheet = XLSX.utils.json_to_sheet(exportData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
                finalBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

            } else if (format === 'CSV') {
                const headers = activeFields.map(f => f.label);
                const keys = activeFields.map(f => f.key);
                let csvContent = headers.join(',') + '\n';
                data.forEach(row => {
                    const values = keys.map(k => {
                        let val = row[k] ?? '';
                        if (val?.seconds) val = new Date(val.seconds * 1000).toLocaleDateString();
                        if (val?.toDate) val = val.toDate().toLocaleDateString();
                        return `"${String(val).replace(/"/g, '""')}"`;
                    });
                    csvContent += values.join(',') + '\n';
                });
                finalBuffer = new TextEncoder().encode(csvContent).buffer;
                mimeType = 'text/csv;charset=utf-8';
                
            } else if (format === 'HTML') {
                const htmlContent = generateHTML(data, activeFields);
                finalBuffer = new TextEncoder().encode(htmlContent).buffer;
                mimeType = 'text/html;charset=utf-8';
            }

            // 4. Save to Disk
            if (fileHandle) {
                // Native System Dialog
                const writable = await fileHandle.createWritable();
                await writable.write(finalBuffer);
                await writable.close();
            } else {
                // Robust Fallback (Data URI to bypass Blob interception)
                const uint8Array = new Uint8Array(finalBuffer);
                let binary = '';
                for (let i = 0; i < uint8Array.length; i++) {
                    binary += String.fromCharCode(uint8Array[i]);
                }
                const base64 = btoa(binary);
                const url = `data:${mimeType};base64,${base64}`;

                const link = document.createElement('a');
                link.href = url;
                link.download = defaultFileName;
                document.body.appendChild(link);
                link.click();
                setTimeout(() => document.body.removeChild(link), 100);
            }

            // 5. Log to Data Transfers Audit Log
            try {
                await addDoc(collection(db, 'companies', companyId, 'data_transfers'), {
                    type: 'EXPORT',
                    module: config.label,
                    fileName: defaultFileName,
                    records: data.length,
                    status: 'Completed',
                    createdAt: serverTimestamp(),
                    createdBy: user?.email || "System User"
                });
            } catch {
                // Ignore silent error
            }

            setTimeout(() => {
                setIsExporting(false);
                onClose();
            }, 800);

        } catch {
            setIsExporting(false);
            alert("Export failed. Please check the console for details.");
        }
    };

    // Notion/HubSpot Hybrid UI Styles
    const inputStyle = {
        width: '100%',
        padding: '8px 12px',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        fontSize: '14px',
        color: 'var(--text-primary)',
        outline: 'none',
        background: 'var(--bg-surface)'
    };

    const selectStyle = {
        ...inputStyle,
        appearance: 'none',
        cursor: 'pointer',
        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        backgroundSize: '16px'
    };

    const labelStyle = {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: 'var(--text-secondary)',
        marginBottom: '6px'
    };

    const radioCardStyle = (active) => ({
        padding: '12px',
        border: `1px solid ${active ? 'var(--text-primary)' : 'var(--border)'}`,
        borderRadius: '8px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        background: active ? 'var(--bg-subtle)' : 'var(--bg-surface)',
        transition: 'all 0.2s'
    });

    return (
        <AnimatePresence>
            {isOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 99999,
                    padding: '20px'
                }}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        style={{
                            background: 'var(--bg-surface)',
                            width: '100%',
                            maxWidth: '520px',
                            borderRadius: '12px',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: '90vh',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Header */}
                        <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>Export view</h2>
                            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: '4px' }} className="hover-color-primary">
                                <ObliqueIcon name="close" size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '24px', overflowY: 'auto' }} className="custom-scrollbar">
                            
                            <div style={{ marginBottom: '24px' }}>
                                <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '16px' }}>
                                    Exporting: <strong>{dataScope === 'CURRENT_VIEW' ? (customData?.length || 0) : 'All'} records</strong> in 1 file
                                </div>
                                
                                <label style={labelStyle}>Export name</label>
                                <input 
                                    type="text" 
                                    value={exportName}
                                    onChange={(e) => setExportName(e.target.value)}
                                    style={inputStyle}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                                <div>
                                    <label style={labelStyle}>File format</label>
                                    <select value={format} onChange={(e) => setFormat(e.target.value)} style={selectStyle}>
                                        <option value="XLSX">Excel (XLSX)</option>
                                        <option value="CSV">CSV</option>
                                        <option value="HTML">HTML Page</option>
                                        <option value="PDF">PDF Table</option>
                                        <option value="DASHBOARD">Premium Dashboard</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>Column headers</label>
                                    <select style={selectStyle} disabled>
                                        <option>English</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={labelStyle}>Data included in export</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div 
                                        style={radioCardStyle(dataScope === 'CURRENT_VIEW')}
                                        onClick={() => setDataScope('CURRENT_VIEW')}
                                    >
                                        <div style={{ marginTop: '2px', color: dataScope === 'CURRENT_VIEW' ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                                            <ObliqueIcon name={dataScope === 'CURRENT_VIEW' ? "radio_button_checked" : "radio_button_unchecked"} size={18} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>Current view</div>
                                            <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '2px' }}>Only includes the {customData?.length || 0} records currently visible after your filters/search.</div>
                                        </div>
                                    </div>
                                    
                                    <div 
                                        style={radioCardStyle(dataScope === 'EVERYTHING')}
                                        onClick={() => setDataScope('EVERYTHING')}
                                    >
                                        <div style={{ marginTop: '2px', color: dataScope === 'EVERYTHING' ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                                            <ObliqueIcon name={dataScope === 'EVERYTHING' ? "radio_button_checked" : "radio_button_unchecked"} size={18} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>Everything in database</div>
                                            <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '2px' }}>Ignores filters and exports the entire raw {config.label} collection.</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <button 
                                    onClick={() => setShowCustomize(!showCustomize)}
                                    style={{ 
                                        display: 'flex', alignItems: 'center', gap: '6px', 
                                        background: 'none', border: 'none', padding: 0, 
                                        color: 'var(--text-primary)', fontSize: '14px', fontWeight: '500', cursor: 'pointer' 
                                    }}
                                >
                                    <ObliqueIcon name={showCustomize ? "expand_more" : "chevron_right"} size={18} />
                                    Customize properties
                                </button>

                                <AnimatePresence>
                                    {showCustomize && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            style={{ overflow: 'hidden', marginTop: '16px' }}
                                        >
                                            <div style={{ padding: '16px', background: 'var(--bg-subtle)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                    <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Included Columns</span>
                                                    <button 
                                                        onClick={() => setSelectedFields(selectedFields.length === config.fields.length ? [] : config.fields.map(f => f.key))}
                                                        style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
                                                    >
                                                        {selectedFields.length === config.fields.length ? 'Deselect all' : 'Select all'}
                                                    </button>
                                                </div>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                    {config.fields.map(field => (
                                                        <label key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer' }}>
                                                            <input 
                                                                type="checkbox"
                                                                checked={selectedFields.includes(field.key)}
                                                                onChange={() => handleFieldToggle(field.key)}
                                                                style={{ accentColor: 'var(--accent)', width: '14px', height: '14px' }}
                                                            />
                                                            {field.label}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                        </div>

                        {/* Footer */}
                        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '12px', background: 'var(--bg-subtle)' }}>
                            <button
                                onClick={handleExport}
                                disabled={isExporting}
                                style={{
                                    flex: 1,
                                    background: 'var(--text-primary)',
                                    color: 'var(--bg-surface)',
                                    border: 'none',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: isExporting ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    opacity: isExporting ? 0.8 : 1
                                }}
                            >
                                {isExporting ? (
                                    <>
                                        <ObliqueIcon name="progress_activity" size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                        Exporting...
                                    </>
                                ) : (
                                    'Export'
                                )}
                            </button>
                            <button
                                onClick={onClose}
                                disabled={isExporting}
                                style={{
                                    flex: 1,
                                    background: 'transparent',
                                    color: 'var(--text-primary)',
                                    border: '1px solid var(--border)',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
